import React, { useRef, useState, useEffect } from "react";
import ReactDOM from "react-dom";
import { inject, observer } from "mobx-react";
import { injectIntl, WrappedComponentProps } from "react-intl";
import NodeChatStore from "common/broadcast/_stores/nodechat/NodeChatStore";
import { LovenseToyStatus } from "common/broadcast/_stores/nodechat/types";
import { Icon } from "@material-ui/core";

interface LovenseToyStatusBarProps {
  nodeChatStore?: NodeChatStore;
}

const LovenseToyStatusBar: React.FunctionComponent<LovenseToyStatusBarProps & WrappedComponentProps> = ({
  nodeChatStore,
  intl,
}) => {
  const { lovenseToyStatuses } = nodeChatStore!;
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);
  const [selectedToyId, setSelectedToyId] = useState<string | null>(null);
  const [tooltipPosition, setTooltipPosition] = useState<{ top: number; left: number } | null>(null);
  const tooltipRef = useRef<HTMLDivElement>(null);
  const cardRefs = useRef<{ [key: string]: HTMLDivElement }>({});

  // Check scroll position and update chevron visibility
  const updateScrollButtons = () => {
    if (scrollContainerRef.current) {
      const container = scrollContainerRef.current;
      const scrollLeft = container.scrollLeft;
      const scrollWidth = container.scrollWidth;
      const clientWidth = container.clientWidth;

      setCanScrollLeft(scrollLeft > 0);
      setCanScrollRight(scrollLeft < scrollWidth - clientWidth - 1);
    }
  };

  // Set up scroll listener
  useEffect(() => {
    const container = scrollContainerRef.current;
    if (container) {
      updateScrollButtons();
      container.addEventListener("scroll", updateScrollButtons);

      // Also update on window resize
      window.addEventListener("resize", updateScrollButtons);

      return () => {
        container.removeEventListener("scroll", updateScrollButtons);
        window.removeEventListener("resize", updateScrollButtons);
      };
    }
  }, [lovenseToyStatuses]);

  // Handle clicking outside tooltip to close it
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;
      if (tooltipRef.current && !tooltipRef.current.contains(target)) {
        // Check if click is on a toy card
        const isClickOnCard = Object.values(cardRefs.current).some(
          (card) => card && card.contains(target)
        );
        if (!isClickOnCard) {
          setSelectedToyId(null);
          setTooltipPosition(null);
        }
      }
    };

    if (selectedToyId) {
      document.addEventListener("mousedown", handleClickOutside);
      return () => {
        document.removeEventListener("mousedown", handleClickOutside);
      };
    }
  }, [selectedToyId]);

  // Update tooltip position on scroll or resize
  useEffect(() => {
    const updateTooltipPosition = () => {
      if (selectedToyId && cardRefs.current[selectedToyId]) {
        const rect = cardRefs.current[selectedToyId].getBoundingClientRect();
        const viewportWidth = window.innerWidth;
        const padding = 16;

        // Get actual tooltip width - use a larger estimate as fallback
        const tooltipWidth = tooltipRef.current
          ? tooltipRef.current.offsetWidth
          : 250;

        // Start with left-aligned to card
        let left = rect.left;

        // Check if it would overflow viewport
        if (left + tooltipWidth + padding > viewportWidth) {
          // Position from right edge of viewport instead
          left = viewportWidth - tooltipWidth - padding;
        }

        // Ensure it doesn't go off left edge
        if (left < padding) {
          left = padding;
        }

        setTooltipPosition({
          top: rect.top,
          left: left,
        });
      }
    };

    if (selectedToyId) {
      updateTooltipPosition();

      window.addEventListener("scroll", updateTooltipPosition, true);
      window.addEventListener("resize", updateTooltipPosition);

      return () => {
        window.removeEventListener("scroll", updateTooltipPosition, true);
        window.removeEventListener("resize", updateTooltipPosition);
      };
    }
  }, [selectedToyId, lovenseToyStatuses]);

  // Toggle tooltip visibility
  const handleToyClick = (toyId: string) => {
    if (selectedToyId === toyId) {
      setSelectedToyId(null);
      setTooltipPosition(null);
    } else {
      setSelectedToyId(toyId);
      // Position will be calculated in useEffect
    }
  };

  // Scroll to previous toy
  const scrollPrev = () => {
    if (scrollContainerRef.current) {
      const container = scrollContainerRef.current;
      container.scrollBy({ left: -120, behavior: "smooth" });
    }
  };

  // Scroll to next toy
  const scrollNext = () => {
    if (scrollContainerRef.current) {
      const container = scrollContainerRef.current;
      container.scrollBy({ left: 120, behavior: "smooth" });
    }
  };

  const renderToyStatus = (toy: LovenseToyStatus, index: number) => {
    const isConnected = toy.status === "on";
    const toyName = toy.name || toy.type || `Toy ${index + 1}`;
    const toyId = toy.id || `toy-${index}`;

    return (
      <div
        key={toyId}
        ref={(el) => {
          if (el) cardRefs.current[toyId] = el;
        }}
        onClick={() => handleToyClick(toyId)}
        className="flex items-center gap-2 px-3 py-1.5 bg-primary-bg-color rounded-md flex-shrink-0 cursor-pointer hover:opacity-80 transition-opacity"
      >
        <div
          className={`w-3 h-3 rounded-full flex-shrink-0 ${
            isConnected ? "bg-green-500" : "bg-red-500"
          }`}
        />
        <span className="text-sm text-primary-text-color font-medium whitespace-nowrap">
          {toyName}
        </span>
      </div>
    );
  };

  // Render tooltip via portal
  const renderTooltip = () => {
    if (!selectedToyId || !tooltipPosition) return null;

    const selectedToy = lovenseToyStatuses.find(
      (toy, index) => (toy.id || `toy-${index}`) === selectedToyId
    );

    if (!selectedToy) return null;

    const isConnected = selectedToy.status === "on";
    const toyName = selectedToy.name || selectedToy.type || "Toy";

    return ReactDOM.createPortal(
      <div
        ref={tooltipRef}
        className="fixed bg-primary-bg-color border border-primary-border-color rounded-md shadow-lg p-3 min-w-48 whitespace-nowrap"
        style={{
          top: `${tooltipPosition.top}px`,
          left: `${tooltipPosition.left}px`,
          transform: "translateY(-100%) translateY(-8px)",
          zIndex: 99999,
        }}
      >
        <div className="flex flex-col gap-2">
          <div className="text-sm font-semibold text-primary-text-color border-b border-primary-border-color pb-2">
            {toyName}
          </div>

          {selectedToy.status && (
            <div className="flex items-center justify-between gap-4">
              <span className="text-xs text-secondary-text-color">
                {intl.formatMessage({
                  id: "lovense-toy.status",
                  defaultMessage: "Status:",
                })}
              </span>
              <div className="flex items-center gap-2">
                <div
                  className={`w-2 h-2 rounded-full ${
                    isConnected ? "bg-green-500" : "bg-red-500"
                  }`}
                />
                <span className="text-sm text-primary-text-color font-medium">
                  {isConnected
                    ? intl.formatMessage({
                        id: "lovense-toy.connected",
                        defaultMessage: "Connected",
                      })
                    : intl.formatMessage({
                        id: "lovense-toy.disconnected",
                        defaultMessage: "Disconnected",
                      })}
                </span>
              </div>
            </div>
          )}

          {selectedToy.battery !== undefined && (
            <div className="flex items-center justify-between gap-4">
              <span className="text-xs text-secondary-text-color">
                {intl.formatMessage({
                  id: "lovense-toy.battery",
                  defaultMessage: "Battery:",
                })}
              </span>
              <span
                className={`text-sm font-medium ${
                  selectedToy.battery <= 20
                    ? "text-red-500"
                    : selectedToy.battery <= 50
                    ? "text-yellow-500"
                    : "text-green-500"
                }`}
              >
                {selectedToy.battery}%
              </span>
            </div>
          )}
        </div>
      </div>,
      document.body
    );
  };

  // Don't render if there are no toys (after all hooks are defined)
  if (!lovenseToyStatuses || lovenseToyStatuses.length === 0) {
    return null;
  }

  return (
    <>
      <div className="flex items-center bg-secondary-bg-color border-t border-primary-border-color relative">
        {canScrollLeft && (
          <button
            onClick={scrollPrev}
            className="flex items-center justify-center flex-shrink-0 sticky left-0 bg-secondary-bg-color px-1 py-2 cursor-pointer hover:bg-primary-bg-color transition-colors z-10"
            title={intl.formatMessage({
              id: "lovense-toy.scrollPrev",
              defaultMessage: "Scroll to previous toy",
            })}
          >
            <Icon className="text-secondary-text-color" fontSize="small">
              chevron_left
            </Icon>
          </button>
        )}
        <span className="text-xs text-secondary-text-color font-semibold uppercase tracking-wide flex-shrink-0 sticky left-0 bg-secondary-bg-color px-4 py-2 z-10">
          {intl.formatMessage({
            id: "lovense-toy.lovense",
            defaultMessage: "Lovense:",
          })}
        </span>
        <div
          ref={scrollContainerRef}
          className="flex items-center gap-2 overflow-x-auto flex-1 py-2 pr-4 lovense-scroll-container"
          style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
        >
          <div className="flex items-center gap-2 flex-nowrap">
            {lovenseToyStatuses.map((toy, index) => renderToyStatus(toy, index))}
          </div>
        </div>
        {canScrollRight && (
          <button
            onClick={scrollNext}
            className="flex items-center justify-center flex-shrink-0 sticky right-0 bg-secondary-bg-color px-1 py-2 cursor-pointer hover:bg-primary-bg-color transition-colors z-10"
            title={intl.formatMessage({
              id: "lovense-toy.scrollNext",
              defaultMessage: "Scroll to next toy",
            })}
          >
            <Icon className="text-secondary-text-color" fontSize="small">
              chevron_right
            </Icon>
          </button>
        )}
      </div>
      {renderTooltip()}
    </>
  );
};

export default injectIntl(
  inject("nodeChatStore")(observer(LovenseToyStatusBar))
);
